import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Sun, Moon, Eye, EyeOff } from 'lucide-react';
import './LoginPage.css';
import { mensajeDeError } from '../utils/errores.js'; // Asegúrate de que importe el CSS correcto

const WHATSAPP_NUMBER = "5215610480746";
import { enlaceWhatsApp } from '../contacto.js';
// El destino de cada quien vive en UN solo sitio, junto al de la raiz: si se
// separan, el alumno aterriza donde no toca segun por donde haya entrado.
import { destinoDe } from '../sesion.js';
const waSupport = `¡Hola! Tengo problemas para acceder a mi cuenta de Spacy.\n\n¿Me pueden ayudar?`;

const openWhatsApp = (msg) => {
    window.open(enlaceWhatsApp(msg, WHATSAPP_NUMBER), '_blank');
};

const WaIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [avatarState, setAvatarState] = useState('intro');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        // Usa data-theme en el html y body para mantener consistencia con otros archivos
        document.documentElement.setAttribute('data-theme', theme); 
        document.body.setAttribute('data-theme', theme); 
        localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
    };

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const resetSuccess = searchParams.get('reset') === 'true';

    const avatarImages = {
        intro: '/intro.png',
        escribir: '/escribir.png',
        contraseña: '/contraseña.png',
        fallo: '/fallo.png',
        listo: '/listo.png'
    };

    const validateForm = () => {
        let isValid = true;
        setEmailError('');
        setPasswordError('');

        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            setEmailError('Escribe un correo válido, como nombre@correo.com.');
            isValid = false;
        }
        if (!password || password.length < 8) {
            setPasswordError('Tu contraseña tiene al menos 8 caracteres.');
            isValid = false;
        }
        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm()) {
            setAvatarState('fallo');
            setTimeout(() => setAvatarState('intro'), 2000);
            return;
        }
        setIsLoading(true);
        try {
            const formData = new URLSearchParams();
            formData.append('username', email);
            formData.append('password', password);
            
            // Inyectamos la URL base dinámica
            const API_BASE_URL = import.meta.env.VITE_API_URL || '';
            const response = await fetch(`${API_BASE_URL}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: formData,
            });
            if (response.ok) {
                setAvatarState('listo');
                const data = await response.json();
                localStorage.setItem('token', data.access_token);
                localStorage.setItem('user', JSON.stringify(data.user));

                // ── A DÓNDE ATERRIZA, LO DECIDE `sesion.js` ──────────────────
                // Y no un `if` aquí. El destino tiene que ser EL MISMO que el de
                // la raíz: si el login manda al coach y `/` al gimnasio, el
                // alumno aterriza en un sitio distinto según por dónde entró.
                //
                // Al alumno lo recibe Spacy (Rob, 27-09-2026); al profesor, su
                // agenda —y no sólo porque abra la app para eso: `/entrenar` y
                // `/coach` exigen `learning_profile`, y un profesor creado desde
                // el CRM no tiene ninguno, así que rebotaría al onboarding de
                // alumno y se quedaría dando vueltas sin ver su panel jamás.
                setTimeout(() => navigate(destinoDe(data.user)), 1500);
            } else {
                const errorData = await response.json();
                if (response.status === 403 && errorData.detail && errorData.detail.includes("inactiva")) {
                    navigate('/verify-email', { state: { email } });
                    return;
                }
                setAvatarState('fallo');
                setPasswordError(mensajeDeError(
                    response, errorData.detail, 'El correo o la contraseña no coinciden.'));
                setTimeout(() => setAvatarState('intro'), 2000);
            }
        } catch {
            setAvatarState('fallo');
            setPasswordError('No se pudo conectar al servidor. Inténtalo más tarde.');
            setTimeout(() => setAvatarState('intro'), 2000);
        } finally {
            setTimeout(() => setIsLoading(false), 1500);
        }
    };

    return (
        // --- CAMBIO: className 'lp-page' a 'logp-page' ---
        <div className="logp-page"> 
            {/* --- CAMBIO: className 'lp-container' a 'logp-container' --- */}
            <div className="logp-container"> 
                {/* --- CAMBIO: prefijos .lp- a .logp- --- */}
                <div className="logp-avatar-container"> 
                    <img src={avatarImages[avatarState]} alt="Login Avatar" className="logp-avatar-image" />
                </div>
                
                {/* --- CAMBIO: prefijos .lp- a .logp- --- */}
                <div className="logp-theme-toggle-container">
                    <button onClick={toggleTheme} className="logp-theme-toggle-btn">
                        {theme === 'light' ? '🌙' : '☀️'}
                    </button>
                </div>

                {/* --- CAMBIO: prefijos .lp- a .logp- --- */}
                <h1 className="logp-title">¡Qué bueno verte!</h1>
                <p className="logp-subtitle">Entra y sigue entrenando</p>

                {resetSuccess && (
                    <div style={{
                        background: 'rgba(0,255,136,0.1)',
                        border: '1px solid rgba(0,255,136,0.3)',
                        borderRadius: '12px',
                        padding: '0.75rem 1rem',
                        marginBottom: '1rem',
                        color: '#00ff88',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                    }}>
                        ✅ Contraseña actualizada. Inicia sesión con tu nueva contraseña.
                    </div>
                )}

                {/* --- CAMBIO: prefijos .lp- a .logp- --- */}
                <form onSubmit={handleSubmit} noValidate className="logp-form">
                    <div className="logp-input-group">
                        <label htmlFor="email" className="visually-hidden">Correo electrónico</label>
                        <input
                            maxLength={254}
                            type="email"
                            id="email"
                            className="logp-input-field" // CAMBIO
                            placeholder="Correo electrónico"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onFocus={() => setAvatarState('escribir')}
                            onBlur={() => setAvatarState('intro')}
                            required
                            name="email"
                            autoComplete="email"
                        />
                        {emailError && <p className="logp-error-message">{emailError}</p>} {/* CAMBIO */}
                    </div>
                    <div className="logp-input-group">
                        {/* --- CAMBIO: prefijos .lp- a .logp- --- */}
                        <div className="logp-input-wrapper"> 
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                className="logp-input-field" // CAMBIO
                                placeholder="Contraseña"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onFocus={() => setAvatarState('contraseña')}
                                onBlur={() => setAvatarState('intro')}
                                required
                                name="password"
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="logp-icon-button" // CAMBIO
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {passwordError && <p className="logp-error-message">{passwordError}</p>} {/* CAMBIO */}
                    </div>
                    {/* --- CAMBIO: prefijos .lp- a .logp- --- */}
                    <div style={{ textAlign: 'right', marginTop: '-0.5rem' }}>
                        <Link
                            to="/forgot-password"
                            style={{
                                fontSize: '0.82rem',
                                color: 'var(--text-secondary)',
                                textDecoration: 'none',
                                fontWeight: 500,
                                transition: 'color 0.2s',
                            }}
                            onMouseEnter={e => e.target.style.color = 'var(--text-primary)'}
                            onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}
                        >
                            ¿Olvidaste tu contraseña?
                        </Link>
                    </div>
                    <button type="submit" className="logp-login-btn" disabled={isLoading}>
                        {isLoading ? 'Entrando…' : 'Entrar'}
                    </button>
                </form>

            </div>

            {/* ── Support Footer ── */}
            <footer className="logp-support-footer">
                <div className="logp-support-card">
                    <div className="logp-support-icon">🛟</div>
                    <div className="logp-support-copy">
                        <strong>¿Problemas con tu acceso?</strong>
                        <span>Soporte disponible de lunes a sábado</span>
                    </div>
                    <button className="logp-wa-ghost" type="button" onClick={() => openWhatsApp(waSupport)}>
                        <WaIcon />
                        Contactar
                    </button>
                </div>

                <div className="logp-footer-links">
                    <span className="logp-footer-text">¿Todavía no tienes cuenta?</span>
                    <Link to="/register" className="logp-footer-link">Créala aquí</Link>
                </div>
            </footer>
        </div>
    );
}

export default LoginPage;